/* eslint-disable */
import {useEffect, useRef, useState} from 'react';
import {Linking, Pressable, ScrollView, StyleSheet, Text, View} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import OnboardingButton from '../../components/onboarding/OnboardingButton';
import StepHeader from '../../components/onboarding/StepHeader';
import PulsingLocation from '../../components/onboarding/PulsingLocation';
import CitySearchSheet from '../../components/onboarding/CitySearchSheet';
import AppIcon from '../../components/onboarding/AppIcon';
import {colors, radii, spacing, typography} from '../../config/theme';
import {
  LOCATION_SERVICES_OFF_MESSAGE,
  canOpenSettings,
  getCurrentLocation,
  hasLocationPermission,
  locationErrorMessage,
  requestLocationPermission,
} from '../../services/location-service';
import {
  getCityCoordinates,
  reverseGeocodeCity,
} from '../../services/city-service';
import {prefetchOnboardingCities} from '../../services/cities-api';
import {useOnboarding} from './OnboardingContext';

// status (GPS flow): 'idle' | 'locating' | 'found' | 'denied' | 'blocked' | 'error'
// selectedCity (manual flow): a city object from the sheet, or null
const SavingsRadar = ({navigation}) => {
  const {t, i18n} = useTranslation();
  const language = (i18n.language || 'en').split('-')[0];
  const {config, update} = useOnboarding();
  const {radiusKm} = config.location;

  const [status, setStatus] = useState('idle');
  const [coords, setCoords] = useState(null);
  const [gpsPlace, setGpsPlace] = useState(null); // {name, region} of the GPS point
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Bumped on every GPS attempt and every city pick, so a GPS result that
  // arrives after the user picked a city is ignored.
  const gpsRequest = useRef(0);

  // While GPS is running, the city chosen earlier is kept but not shown, so it
  // comes back if GPS fails.
  const hasCity = !!selectedCity && status !== 'locating';
  const hasGps = status === 'found' && !!coords;
  const canContinue = hasGps || hasCity;
  const isBad = ['denied', 'blocked', 'error'].includes(status);

  const dotColor = hasCity
    ? colors.secondary
    : isBad
    ? colors.error
    : status === 'found'
    ? colors.secondary
    : colors.primary;

  // ---- actions ----
  const handleAllow = async () => {
    if (status === 'locating') return;
    // The user may have allowed location in Settings since, so ask again first
    // and only send them to Settings if it is still blocked.
    const wasBlocked = status === 'blocked';

    const request = ++gpsRequest.current;
    const isStale = () => request !== gpsRequest.current;
    setStatus('locating');
    setErrorMsg('');
    try {
      const permission = await requestLocationPermission();
      if (isStale()) return;
      if (permission === 'disabled') {
        setErrorMsg(LOCATION_SERVICES_OFF_MESSAGE);
        setStatus('error');
        return;
      }
      if (permission === 'blocked' && wasBlocked && canOpenSettings) {
        Linking.openSettings(); // phone settings (not possible on web)
      }
      if (permission !== 'granted') {
        setStatus(permission === 'blocked' ? 'blocked' : 'denied');
        return;
      }
      const position = await getCurrentLocation();
      if (isStale()) return;
      setCoords(position);
      setGpsPlace(null);
      setSelectedCity(null); // GPS replaces any city chosen earlier
      setStatus('found');

      // Show "Amsterdam, North Holland" instead of raw coordinates. Not
      // needed to continue, so a failure just keeps "Location found".
      reverseGeocodeCity(position, language)
        .then(place => !isStale() && setGpsPlace(place))
        .catch(e => console.log('[onboarding] reverse geocode failed:', e?.message));
    } catch (e) {
      if (isStale()) return;
      if (e?.code === 1) {
        setStatus('blocked'); // refused at the system / browser level
      } else {
        setErrorMsg(locationErrorMessage(e));
        setStatus('error');
      }
    }
  };

  // Start loading the city list now, so it is already there when the user
  // opens the "Select your city or region" sheet.
  useEffect(() => {
    prefetchOnboardingCities(language);
  }, []);

  // If location is already allowed, skip the "Allow location" step and
  // find the user straight away. Never shows a popup (silent check only).
  useEffect(() => {
    let alive = true;
    hasLocationPermission().then(granted => {
      if (alive && granted) handleAllow();
    });
    return () => {
      alive = false;
    };
  }, []);

  // The user picked a city in the sheet.
  const handleCitySelected = city => {
    gpsRequest.current++; // drop any GPS lookup still running
    setSelectedCity(city);
    setCoords(null); // a city replaces any GPS result
    setGpsPlace(null);
    setErrorMsg('');
    setStatus('idle');
    setSheetOpen(false);

    // Cities from the API already have coordinates: nothing more to look up.
    if (city.latitude != null && city.longitude != null) return;

    // Search results have no coordinates yet: look them up in the background.
    getCityCoordinates(city)
      .then(withCoords =>
        setSelectedCity(prev => (prev?.id === city.id ? withCoords : prev)),
      )
      .catch(e => console.log('[onboarding] city coordinates failed:', e?.message));
  };

  // The GPS chip inside the sheet: close the sheet, then run the GPS flow.
  // The city is only replaced once GPS succeeds (see handleAllow).
  const handleUseGpsFromSheet = () => {
    setSheetOpen(false);
    // wait for the sheet to finish closing before the system permission popup (iOS)
    setTimeout(handleAllow, 400);
  };

  // Both modes save coordinates (when known), so nearby stations can be
  // loaded the same way for either.
  // TODO(API): send them to the backend to load real nearby stations.
  const handleContinue = () => {
    update({
      location: hasGps
        ? {mode: 'gps', ...coords, city: gpsPlace?.name || null}
        : {
            mode: 'city',
            city: selectedCity.name,
            cityId: selectedCity.id,
            latitude: selectedCity.latitude ?? null,
            longitude: selectedCity.longitude ?? null,
          },
    });
    navigation.navigate('SavingsSummary');
  };

  // ---- texts that depend on the state ----
  const statusPill = {
    idle: t('onboarding.radar.pillIdle', 'YOUR LOCATION'),
    locating: t('onboarding.radar.pillLocating', 'FINDING YOU…'),
    found: t('onboarding.radar.here', 'YOU ARE HERE'),
  }[status] || t('onboarding.radar.pillOff', 'LOCATION OFF');

  const statusMessage = {
    idle: t(
      'onboarding.radar.msgIdle',
      'Allow location so we can find the cheapest pumps around you.',
    ),
    locating: t('onboarding.radar.msgLocating', 'Finding your location…'),
    found: t('onboarding.radar.msgFound', 'Location found ✓'),
    denied: t(
      'onboarding.radar.msgDenied',
      'Location permission was denied. You can try again or choose your city.',
    ),
    blocked: canOpenSettings
      ? t(
          'onboarding.radar.msgBlocked',
          'Location is blocked for this app. Open settings to allow it, or choose your city.',
        )
      : t(
          'onboarding.radar.msgBlockedWeb',
          'Location is blocked in your browser. Click the lock icon next to the address bar to allow it, then press Try again, or choose your city.',
        ),
    error: errorMsg,
  }[status];

  const pillText = hasCity ? selectedCity.name.toUpperCase() : statusPill;

  // The place we'll search around: the chosen city, or the GPS point's city.
  const place = hasCity ? selectedCity : hasGps ? gpsPlace : null;
  // Shown when there's no place to show, or to report a failed GPS attempt
  // while a city is kept as fallback.
  const message = !place || isBad ? statusMessage : null;

  const manualLink = hasCity
    ? t('onboarding.radar.changeCity', 'Change city')
    : hasGps
    ? t('onboarding.radar.notHere', 'Not here? Choose your city')
    : t('onboarding.radar.manual', 'Or type city manually');

  // ---- main button ----
  let buttonTitle;
  let onButtonPress;
  if (canContinue) {
    buttonTitle = t('onboarding.radar.continue', 'CONTINUE →');
    onButtonPress = handleContinue;
  } else {
    onButtonPress = handleAllow;
    if (status === 'locating') {
      buttonTitle = t('onboarding.radar.locating', 'FINDING YOU…');
    } else if (status === 'blocked' && canOpenSettings) {
      buttonTitle = t('onboarding.radar.openSettings', 'OPEN SETTINGS');
    } else if (['denied', 'error', 'blocked'].includes(status)) {
      buttonTitle = t('onboarding.radar.tryAgain', 'TRY AGAIN');
    } else {
      buttonTitle = t('onboarding.radar.allow', 'ALLOW LOCATION');
    }
  }

  return (
    <SafeAreaView style={styles.safe}>
      <StepHeader
        title={t('onboarding.savingsTarget', 'Savings Target')}
        step={3}
        total={config.totalSteps}
        onBack={() => navigation.goBack()}
      />

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}>
        <Text style={styles.title}>
          {t('onboarding.radar.title', 'SCAN')}{' '}
          <Text style={styles.titleAccent}>
            {t('onboarding.radar.titleAccent', 'CHEAP PUMPS')}
          </Text>{' '}
          {t('onboarding.radar.titleEnd', 'NEAR YOU')}
        </Text>
        <Text style={styles.subtitle}>
          {t('onboarding.radar.subtitle', 'We check live prices within a')}{' '}
          <Text style={styles.subtitleBold}>{radiusKm} km</Text>{' '}
          {t('onboarding.radar.circle', 'circle.')}
        </Text>

        {/* Current location: just a blinking marker, no map */}
        <View style={styles.locationCard}>
          <PulsingLocation color={dotColor} />
          <View style={[styles.pill, {backgroundColor: dotColor}]}>
            <Text style={styles.pillText}>{pillText}</Text>
          </View>
        </View>

        {!!place && (
          <View style={styles.place}>
            <View style={styles.placeNameRow}>
              <AppIcon name="pin" size={20} color={colors.primary} />
              <Text style={styles.placeName}>{place.name}</Text>
            </View>
            <Text style={styles.placeSub}>
              {place.region ? `${place.region} • ` : ''}
              {t('onboarding.radar.placeRadius', 'Pumps within {{km}} km', {
                km: radiusKm,
              })}
            </Text>
          </View>
        )}
        {!!message && (
          <Text style={[styles.message, isBad && styles.messageBad]}>
            {message}
          </Text>
        )}

        <View style={styles.privacy}>
          <AppIcon name="lock" size={14} color={colors.primary} />
          <Text style={styles.privacyText}>
            <Text style={styles.privacyBold}>
              {t('onboarding.radar.private', '100% PRIVATE')}
            </Text>
            {'  •  '}
            {t(
              'onboarding.radar.privateSub',
              'Never sold or background tracked',
            )}
          </Text>
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <OnboardingButton title={buttonTitle} onPress={onButtonPress} />
        <Pressable
          onPress={() => setSheetOpen(true)}
          hitSlop={10}
          style={styles.manualRow}>
          <AppIcon name="building" size={16} color={colors.textMuted} />
          <Text style={styles.manual}>{manualLink}</Text>
        </Pressable>
      </View>

      <CitySearchSheet
        visible={sheetOpen}
        onClose={() => setSheetOpen(false)}
        initialCity={selectedCity}
        onSelectCity={handleCitySelected}
        onUseGps={handleUseGpsFromSheet}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safe: {flex: 1, backgroundColor: colors.background},
  scroll: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
    gap: spacing.md,
  },
  title: {
    ...typography.displayMobile,
    color: colors.primary,
    textAlign: 'center',
  },
  titleAccent: {color: colors.secondary},
  subtitle: {
    ...typography.bodyMd,
    color: colors.textMuted,
    textAlign: 'center',
  },
  subtitleBold: {...typography.labelLg, color: colors.primary},

  locationCard: {
    height: 240,
    borderRadius: radii.card,
    backgroundColor: colors.surfaceMuted,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  pill: {
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 2,
  },
  pillText: {...typography.labelSm, color: colors.onPrimary},

  message: {
    ...typography.bodyMd,
    color: colors.textMuted,
    textAlign: 'center',
  },
  messageBad: {color: colors.error},
  place: {alignItems: 'center', gap: spacing.xs},
  placeNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  placeName: {...typography.headlineSm, color: colors.primary},
  placeSub: {
    ...typography.bodyMd,
    color: colors.textMuted,
    textAlign: 'center',
  },

  privacy: {
    backgroundColor: colors.surfaceMuted,
    borderRadius: radii.pill,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md + 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  privacyText: {
    ...typography.labelSm,
    color: colors.textMuted,
    flexShrink: 1,
    textAlign: 'center',
  },
  privacyBold: {color: colors.primary},

  footer: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.sm,
    paddingBottom: spacing.lg,
    backgroundColor: colors.background,
    gap: spacing.md,
  },
  manualRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  manual: {
    ...typography.labelMd,
    color: colors.textMuted,
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
});

export default SavingsRadar;